/**
 * Simple EventEmitter implementation for the browser
 * This polyfills the Node.js events module functionality needed by Google API libraries
 */
export class EventEmitter {
  constructor() {
    this._events = {};
  }

  on(event, listener) {
    if (!this._events[event]) {
      this._events[event] = [];
    }
    this._events[event].push(listener);
    return this;
  }

  once(event, listener) {
    const onceWrapper = (...args) => {
      listener(...args);
      this.removeListener(event, onceWrapper);
    };
    onceWrapper._original = listener;
    return this.on(event, onceWrapper);
  }

  off(event, listener) {
    return this.removeListener(event, listener);
  }

  removeListener(event, listener) {
    if (!this._events[event]) return this;

    const index = this._events[event].findIndex(
      (l) => l === listener || (l._original && l._original === listener)
    );

    if (index !== -1) {
      this._events[event].splice(index, 1);
    }
    return this;
  }

  removeAllListeners(event) {
    if (event) {
      this._events[event] = [];
    } else {
      this._events = {};
    }
    return this;
  }

  emit(event, ...args) {
    if (!this._events[event]) return false;

    this._events[event].forEach((listener) => {
      listener.apply(this, args);
    });

    return true;
  }

  listenerCount(event) {
    return this._events[event] ? this._events[event].length : 0;
  }

  listeners(event) {
    return this._events[event] ? [...this._events[event]] : [];
  }
}

// Set up global polyfill if we're in a browser environment
if (typeof window !== "undefined") {
  // Create a events polyfill module
  if (!window.events) {
    window.events = {
      EventEmitter,
    };
  }

  // For direct Node.js module imports (node:events)
  if (!window.node_events) {
    window.node_events = {
      EventEmitter,
    };
  }

  // Make the EventEmitter available globally
  if (!window.EventEmitter) {
    window.EventEmitter = EventEmitter;
  }

  console.log("Events polyfill initialized for browser environment");
}

export default EventEmitter;
